import { h } from 'preact';
import { useEffect, useState, useReducer } from 'preact/hooks';
import Beans3 from '@/beans';
import { BeansLiquidEngine } from '@/base/helpers';
import useTemplet from '@/bamboo/hooks';
import './ActivePage.css';

const getMemberName = (member) => (member.first_name ? `${member.first_name} ${member.last_name}` : member.email);

export default function ActivePage(props) {
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [emailAddressInput, setEmailAddressInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const { onRegister, onLogin, error, Strings, account } = props;

  const { templet, templetError, templetLoading } = useTemplet({ ruleUid: 'rule:bamboo:display' });

  const formatReferrals = (rawReferrals) => {
    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    rawReferrals.map((referral) => {
      referral.friendName = referral.friend_account ? getMemberName(referral.friend_account) : '';
      referral.createdText = new Date(referral.created).toLocaleDateString('en-US', dateOptions);
      return referral;
    });
    return rawReferrals;
  };

  useEffect(() => {
    if (account) {
      const promises = [Beans3.get({ path: 'ultimate/account/current/referral/' })];

      if (Beans3._trellis_version >= 20240501) {
        promises.push(Beans3.get({ path: `${Beans3._trellis}/discount/?purpose=referral`, namespace: 'trellis' }));
      }

      Promise.all(promises)
        .then(([referralsObj, discountsObj]) => {
          setReferrals(formatReferrals(referralsObj.data));
          if (discountsObj) {
            setRewards((r) => [...r, ...discountsObj.data]);
          }
        })
        .catch((err) => console.error('Failed to fetch data', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setEmailAddressInput('');
  }, [sent]);

  const getReferralLink = () => {
    let referralLink = account.referral_link;
    if (Beans3.previewSession.isActive()) referralLink = `${referralLink}?beans-mode=preview`;
    return referralLink;
  };

  const handleCopyLink = () => {
    if (copied) return;
    Beans3._utils.copyToClipboard(getReferralLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const handleChange = (e) => {
    setEmailAddressInput(e.target.value);
  };

  const handleSendLink = () => {
    if (sent || !emailAddressInput) return;
    setInputError(''); // Clear inputError message state
    Beans3.post({ path: 'bamboo/invite', data: { contact: emailAddressInput, channel: 'email' } })
      .then(() => {
        setSent(true);
        setTimeout(() => setSent(false), 2500);
      })
      .catch((inviteError) => {
        setInputError(inviteError.error?.message || inviteError.error);
      });
  };

  const handleKeyPressed = (e) => {
    if (e.key === 'Enter') {
      handleSendLink();
    }
  };

  const onTabChange = (tag) => {
    props.tabulator.onChange(tag);
    forceUpdate();
  };

  if (loading || templetLoading) {
    return <p style={{ textAlign: 'center' }}>{Strings.loading?.loading_page}</p>;
  }

  if (error || templetError) {
    return (
      <p style={{ textAlign: 'center' }}>
        Something went wrong.{' '}
        <a href="#" className={{ cursor: 'pointer' }}>
          Please contact the Beans team.
        </a>
      </p>
    );
  }

  if (templet) {
    window.Beans3.Bamboo.Actions = {
      ...window.Beans3.Bamboo.Actions,
      onLogin,
      onRegister,
      handleChange,
      handleCopyLink,
      handleSendLink,
      handleKeyPressed,
      onTabChange,
      tabulator: props.tabulator,
    };

    const renderedTempletHTML = BeansLiquidEngine.parseAndRenderSync(templet.html, {
      ...props,
      loading,
      copied,
      sent,
      referrals,
      rewards,
      emailAddressInput,
      inputError,
      activeTab: props.tabulator.getActive(),
      Params: templet.settings,
    });

    return (
      <div className="bamboo-page bamboo-page-active">
        <div dangerouslySetInnerHTML={{ __html: renderedTempletHTML }} />
      </div>
    );
  }
}
